import {useMemo} from 'react';
import {Node, OnEdgesChange, OnNodesChange} from 'reactflow';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {ConditionGroup} from 'studies/typed/shared/useExperimentDesignState';

import {ProcedureRunDesignFlowchartAddReplicateNodeData} from './ProcedureRunDesignFlowchartAddReplicateNode';
import {ProcedureRunDesignFlowchartColumnNodeData} from './ProcedureRunDesignFlowchartColumnNode';
import {getNodeConnectionRoles} from './procedureRunDesignFlowchartConnectionUtils';
import {ProcedureRunDesignFlowchartGhostNodeData} from './ProcedureRunDesignFlowchartGhostNode';
import {
  FlowchartEdgeSpec,
  GridLayoutOptions,
  makeAddReplicateRow,
  makeFlowchartEdges,
  makeInitialNodes,
  StatusByNodeId,
} from './ProcedureRunDesignFlowchartLayoutUtils';
import {ProcedureRunDesignFlowchartNodeData} from './ProcedureRunDesignFlowchartNode';
import {useProcedureRunDesignFlowchartConnectionRoles} from './useProcedureRunDesignFlowchartConnectionRoles';
import {
  ProcedureRunDesignFlowchartLayoutEdge,
  useProcedureRunDesignFlowchartEdges,
} from './useProcedureRunDesignFlowchartEdges';
import type {MethodHighlightState} from './useProcedureRunDesignFlowchartHighlighting';
import {useProcedureRunDesignFlowchartHighlighting} from './useProcedureRunDesignFlowchartHighlighting';
import {useProcedureRunDesignFlowchartMeasuredLayout} from './useProcedureRunDesignFlowchartMeasuredLayout';
import {useProcedureRunDesignFlowchartNodes} from './useProcedureRunDesignFlowchartNodes';

export interface ProcedureRunDesignFlowchartLayoutOptions extends GridLayoutOptions {
  columnHeaderBottomGap: number;
}

export type ProcedureRunDesignFlowchartLayoutNodeData =
  | ProcedureRunDesignFlowchartNodeData
  | ProcedureRunDesignFlowchartColumnNodeData
  | ProcedureRunDesignFlowchartAddReplicateNodeData
  | ProcedureRunDesignFlowchartGhostNodeData;
export type ProcedureRunDesignFlowchartLayoutNode = Node<ProcedureRunDesignFlowchartLayoutNodeData>;

export interface UseProcedureRunDesignFlowchartLayoutArgs {
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  activeMethodId: string | null;
  procedureType: ProcedureType;
  layoutOptions: ProcedureRunDesignFlowchartLayoutOptions;
  methodHighlightState: MethodHighlightState;
  // When false, persisted conditions render with locked icon
  allowEditingPersistedConditions: boolean;
  statusByNodeId?: StatusByNodeId;
  // The single edge list to render (derived + drawn, already merged upstream).
  edgeSpecs: Array<FlowchartEdgeSpec>;
  // Whether the flowchart is being edited. Gates the add-replicate preview row.
  isEditable: boolean;
}

export interface UseProcedureRunDesignFlowchartLayoutResult {
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  edges: Array<ProcedureRunDesignFlowchartLayoutEdge>;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
}

/**
 * Manages the two-phase layout for ProcedureRunDesignFlowchart:
 * 1. Create hidden placeholder nodes so ReactFlow can measure their dimensions.
 * 2. Once dimensions are available, compute grid positions and unhide nodes.
 */
export function useProcedureRunDesignFlowchartLayout({
  conditions,
  methods,
  activeMethodId,
  procedureType,
  layoutOptions,
  methodHighlightState,
  allowEditingPersistedConditions,
  statusByNodeId,
  edgeSpecs,
  isEditable,
}: UseProcedureRunDesignFlowchartLayoutArgs): UseProcedureRunDesignFlowchartLayoutResult {
  // Once a procedure (with methods) is attached, a procedure run design always has at least one
  // condition. The procedure-less empty state renders no methods, so it needs no conditions.
  assert(methods.length === 0 || conditions.length >= 1, 'Expected at least one condition');

  const {columnNodes: initialColumnNodes, rows: gridRows} = useMemo(
    () =>
      makeInitialNodes(
        conditions,
        methods,
        allowEditingPersistedConditions,
        procedureType,
        activeMethodId,
        statusByNodeId,
        edgeSpecs
      ),
    [
      conditions,
      methods,
      allowEditingPersistedConditions,
      activeMethodId,
      procedureType,
      statusByNodeId,
      edgeSpecs,
    ]
  );

  // The add-replicate preview row renders only while editing a split-pool run (active method present).
  const addReplicateRow = useMemo(
    () =>
      isEditable && activeMethodId != null
        ? makeAddReplicateRow({methods, activeMethodId})
        : {row: [], previewEdgeSpecs: []},
    [isEditable, activeMethodId, methods]
  );

  // Append the preview row as the last band so it lands below the deepest column's last real cell.
  const initialRows = useMemo(
    (): Array<Array<ProcedureRunDesignFlowchartLayoutNode>> =>
      addReplicateRow.row.length > 0 ? [...gridRows, addReplicateRow.row] : gridRows,
    [gridRows, addReplicateRow.row]
  );

  const {nodes, setNodes, onNodesChange} = useProcedureRunDesignFlowchartNodes({
    initialColumnNodes,
    initialRows,
  });

  // Roles depend only on methods and the active layer, not live edges. Recomputing here
  // keeps removability in sync with the drawing affordance.
  const roleByNodeId = useMemo(
    () => getNodeConnectionRoles(conditions, methods, [], activeMethodId).roleByNodeId,
    [conditions, methods, activeMethodId]
  );

  // Preview specs carry synthetic ids absent from roleByNodeId, so their edges stay non-removable.
  const initialEdges = useMemo(
    () => makeFlowchartEdges([...edgeSpecs, ...addReplicateRow.previewEdgeSpecs], roleByNodeId),
    [edgeSpecs, addReplicateRow.previewEdgeSpecs, roleByNodeId]
  );

  const {edges, setEdges, onEdgesChange} = useProcedureRunDesignFlowchartEdges({initialEdges});

  useProcedureRunDesignFlowchartHighlighting({
    methodHighlightState,
    initialColumnNodes,
    initialRows,
    setNodes,
  });

  useProcedureRunDesignFlowchartConnectionRoles({
    conditions,
    methods,
    activeMethodId,
    edges,
    initialColumnNodes,
    initialRows,
    setNodes,
  });

  useProcedureRunDesignFlowchartMeasuredLayout({
    initialColumnNodes,
    initialRows,
    layoutOptions,
    nodes,
    setNodes,
    setEdges,
  });

  return {nodes, edges, onNodesChange, onEdgesChange};
}
